import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import AspectRatio from 'components/utils/AspectRatio'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import ModalContext from 'contexts/ModalContext'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import TextLink from 'LuxKit/TextLink'
import SolidSquareShapeIcon from 'LuxKit/Icons/solid/SolidSquareShapeIcon'
import CabinDetailsModal from './CabinDetailsModal'
import useCruiseCabinDescriptions from 'hooks/Cruise/useCruiseCabinDescriptions'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'

const Tile = styled.div`
  width: 100%;
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(20)};
  margin-top: ${rem(20)};

  ${mediaQueryUp.desktop} {
    &:first-child {
      margin-top: 0;
    }
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-column-gap: ${rem(24)};
  grid-template-columns: 2fr 4fr;
`

const MEDIA_IMAGE_PARAMS: ImageParams = {
  quality: 'eco',
  mobileAspectRatio: '4:3',
  tabletAspectRatio: '4:3',
  desktopAspectRatio: '4:3',
  largeDesktopAspectRatio: '4:3',
  mobileWidth: '600vw',
  tabletWidth: '600vw',
  desktopWidth: '160px',
  largeDesktopWidth: '160px',
}

export interface CabinsToDisplay {
  [key: string]: {
    images: Array<{ id: string, type: 'image' }>
    colors: Array<string>
    info: App.CruiseShipCabinCategory
  }
}

interface Props {
  cabin: CabinsToDisplay[keyof CabinsToDisplay]
  cabinCategories?: Array<App.CruiseShipCabinCategory>
  showPackageOptions?: () => void
}
function CabinTile({ cabin, cabinCategories, showPackageOptions }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const descriptions = useCruiseCabinDescriptions()
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const cabinTypeLabel = cabinTypeLabels[cabin.info.cabinType] ?? cabin.info.cabinType

  const handleSelectDate = useCallback(() => {
    showPackageOptions?.()
  }, [showPackageOptions])

  const openCabinDetailsModal = useCallback(() => {
    const modalCategories = cabinCategories?.filter(category => category.cabinType === cabin.info.cabinType) ?? []
    showModal(<CabinDetailsModal
      modalCategories={modalCategories}
      cabinType={cabin.info.cabinType}
    />)
  }, [cabin.info.cabinType, showModal, cabinCategories])

  const handleToggleModal = useCallback(() => {
    if (!cabinCategories) return
    openCabinDetailsModal()
  }, [cabinCategories, openCabinDetailsModal])

  const selectDateLabel = intl.formatMessage({
    id: '0lLP6iTDF8r3igoBEplN',
    defaultMessage: 'Select a date',
    description: 'Button text to select a date for cruise cabin booking',
  })

  const showAllCabinsLabel = intl.formatMessage(
    {
      id: 'I2RHq-gACmlFL2MDKDyU',
      defaultMessage: 'Show all {cabinType} cabins',
      description: 'Hyperlink text to show all cabins of a specific type',
    },
    { cabinType: cabinTypeLabel },
  )

  return (
    <Tile>
      <Group direction="vertical" gap={16}>
        <GridContainer key={cabin.info.imageId}>
          <AspectRatio ratio="4:3">
            <ImageCarousel
              eagerLoadFirstImage
              images={cabin.images}
              imageParams={MEDIA_IMAGE_PARAMS}
            />
          </AspectRatio>

          <Group direction="vertical" gap={8}>
            <Group direction="horizontal" horizontalAlign="space-between">
              <Group direction="vertical" gap={8}>
                <Heading variant="heading5" as="span">{cabinTypeLabel}</Heading>
                <Group direction="horizontal" wrap="wrap">
                  {cabin.colors?.map((color, index) => (
                    <SolidSquareShapeIcon
                      style={{ color }}
                      key={`${color}${index}`}
                    />
                  ))}
                </Group>
              </Group>

              {showPackageOptions && (
                <CSSBreakpoint min="tablet">
                  <TextButton
                    size="medium"
                    kind="secondary"
                    onClick={handleSelectDate}
                  >
                    {selectDateLabel}
                  </TextButton>
                </CSSBreakpoint>
              )}
            </Group>

            <CSSBreakpoint min="tablet">
              <BodyText
                variant="medium"
                className="cabin-info-desktop"
              >
                {descriptions[cabin.info.cabinType]}
              </BodyText>

              <TextLink
                size="small"
                onClick={handleToggleModal}
              >
                {showAllCabinsLabel}
              </TextLink>
            </CSSBreakpoint>
          </Group>
        </GridContainer>

        <CSSBreakpoint max="mobile">
          <Group direction="vertical" gap={16}>
            <BodyText
              variant="medium"
              className="cabin-info-desktop"
            >
              {descriptions[cabin.info.cabinType]}
            </BodyText>

            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
              <TextLink
                onClick={handleToggleModal}
                size="small"
              >
                {showAllCabinsLabel}
              </TextLink>

              {showPackageOptions && (
                <TextButton
                  size="medium"
                  kind="secondary"
                  onClick={handleSelectDate}
                >
                  {selectDateLabel}
                </TextButton>
              )}
            </Group>
          </Group>
        </CSSBreakpoint>
      </Group>
    </Tile>
  )
}

export default CabinTile
